import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
// local
import {CustomTooltip} from "components/CustomTooltip";
import {
  IconBriefcase,
  IconOutbox,
  IconContract,
  IconReply,
  IconSignal,
} from "components/icons";
import {InitialsBadge} from "components/InitialsBadge";
import {color} from "constants/color.consts";
import cn from "classnames";
import {getFullName} from "helpers/helper";

export const OrganizationStats = ({
  projectsCount,
  rfpsCount,
  receivedOffersCount,
  contractsCount,
  contractsSum,
  contacts,
  currentUser,
  addClass,
}) => {
  const {t} = useTranslation();
  const isGrayIcon =
    !projectsCount && !rfpsCount && !receivedOffersCount && !contractsCount;
  const iconColor = isGrayIcon ? color.lightGray : color.black;

  const contactsUsersCurrentUser = useMemo(
    () => contacts?.find((contact) => contact?.id === currentUser?.OrganizationMemberId),
    [contacts, currentUser]
  );
  const sortedContacts = useMemo(
    () => [
      ...(contacts?.sort((contact) =>
        contact?.id === currentUser?.OrganizationMemberId ? -1 : 1
      ) ?? []),
    ],
    [contacts, currentUser?.OrganizationMemberId]
  );
  const ourOrganizationUsersCounter = useMemo(
    () => (contacts?.length > 1 ? contacts?.length - 1 : null),
    [contacts?.length]
  );
  const initialsTooltip = useMemo(
    () =>
      contacts?.length > (!!contactsUsersCurrentUser ? 2 : 1)
        ? t("ORGANIZATION_STATS_CONTACTS_TOOLTIP")?.format(
            sortedContacts
              .slice(1)
              .map((contact) =>
                getFullName(contact.User.firstName, contact.User.lastName)
              )
              .join(", ")
          )
        : null,
    [contacts, contactsUsersCurrentUser, t, sortedContacts]
  );

  const statsData = [
    {
      tooltip: "ORGANIZATION_STATS_PROJECTS_TOOLTIP",
      count: projectsCount,
      icon: <IconBriefcase color={iconColor} width={11} height={12} />,
    },
    {
      tooltip: "ORGANIZATION_STATS_RFP_TOOLTIP",
      count: rfpsCount,
      icon: <IconOutbox color={iconColor} width={11} height={12} />,
    },
    {
      tooltip: "ORGANIZATION_STATS_OFFERS_TOOLTIP",
      count: receivedOffersCount,
      icon: <IconReply color={iconColor} width={11} height={12} />,
    },
    {
      tooltip: "ORGANIZATION_STATS_CONTRACTS_TOOLTIP",
      count: contractsCount,
      icon: <IconContract color={iconColor} width={11} height={12} />,
    },
    {
      customTooltip: `(${contractsSum} SEK) ${t(
        "ORGANIZATION_STATS_CONTRACTS_SUM_TOOLTIP"
      )}`,
      count: (
        <div style={{marginBottom: "-4px"}}>
          {`${contractsSum ? Math.round(contractsSum / 1000) : 0} K`}
          <span className={`ml-1 ${isGrayIcon ? "light-gray" : "discrete-text"}`}>
            SEK
          </span>
        </div>
      ),
      icon: <IconSignal color={iconColor} addClass="ml-2" />,
    },
  ];

  const contact = sortedContacts?.at(0);
  const contactUser = contact?.User ?? {firstName: "", lastName: ""};

  return (
    <div
      className={cn("organization-card_body__name  __stats", {[addClass]: !!addClass})}
    >
      {contacts?.length && (
        <CustomTooltip text={initialsTooltip} placement="left">
          <span className="df-row-center">
            <InitialsBadge
              firstName={contactUser.firstName}
              lastName={contactUser.lastName}
              isCurrentUser={!!contactsUsersCurrentUser}
              counter={ourOrganizationUsersCounter}
            />
          </span>
        </CustomTooltip>
      )}
      {statsData.map((item, index) => {
        // TODO: rethink how to remove index and use unique key
        return (
          <CustomTooltip
            key={`stat-${item.tooltip}-${index}`}
            text={item.customTooltip ?? t(item.tooltip)}
            noWrapText
          >
            <div className="df-row-center">
              {item.icon}
              <span className={`pr-2 fz-10 __bold ${isGrayIcon ? "light-gray" : ""}`}>
                {item.count ?? 0}
              </span>
            </div>
          </CustomTooltip>
        );
      })}
    </div>
  );
};
